import React from "react";
import { Card, CardContent } from "./ui/card";
import { 
  Server, 
  Clock, 
  MessageCircle, 
  Users, 
  AlertTriangle,
  Shield,
  Zap,
  Headphones
} from "lucide-react";

const ServersSection = () => {
  const features = [
    {
      icon: Server,
      title: "Servidores Monitorados 24/7",
      description: "Infraestrutura robusta com monitoramento contínuo para garantir 99.9% de uptime.",
      highlight: "99.9% Uptime"
    },
    {
      icon: Zap,
      title: "Atendimento Automatizado Rápido",
      description: "Sistema de resposta automática para situações comuns, com resolução em segundos.",
      highlight: "< 30s"
    },
    {
      icon: Users,
      title: "Suporte Humano Presente",
      description: "Equipe técnica especializada disponível para atendimento personalizado e cordial.",
      highlight: "24/7"
    },
    {
      icon: AlertTriangle,
      title: "Tonalidades de Emergência",
      description: "Protocolos especiais ativados automaticamente em situações críticas e emergenciais.",
      highlight: "Emergência"
    }
  ];

  return (
    <section className="py-24 px-4 relative overflow-hidden">
      {/* Background with orange/yellow gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-600/90 to-yellow-500/80"></div>
      <div className="absolute inset-0 bg-black/20"></div>
      
      {/* Animated background elements */}
      <div className="absolute top-10 left-10 w-32 h-32 bg-yellow-400/10 rounded-full blur-xl animate-pulse"></div>
      <div className="absolute bottom-10 right-10 w-48 h-48 bg-orange-500/10 rounded-full blur-2xl animate-pulse"></div>
      
      <div className="container mx-auto relative z-10">
        {/* Section header */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <div className="w-20 h-20 bg-black/20 rounded-full flex items-center justify-center backdrop-blur-sm">
              <Shield className="w-10 h-10 text-white" />
            </div>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Infraestrutura Robusta e <br />
            <span className="text-black">Suporte Humano</span>
          </h2>
          
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            Nossa tecnologia é suportada por uma infraestrutura de alto nível e 
            uma equipe dedicada ao seu sucesso.
          </p>
        </div>

        {/* Features grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {features.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <Card 
                key={index}
                className="bg-black/40 border-white/20 backdrop-blur-sm hover:bg-black/50 transition-all duration-300 hover:scale-105 group"
              >
                <CardContent className="p-6 text-center">
                  <div className="mx-auto mb-4 w-16 h-16 bg-white/10 rounded-full flex items-center justify-center group-hover:bg-white/20 transition-all duration-300">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  
                  <div className="mb-3">
                    <span className="inline-block px-3 py-1 bg-white/20 rounded-full text-white text-sm font-semibold">
                      {feature.highlight}
                    </span>
                  </div>
                  
                  <h3 className="text-lg font-bold text-white mb-3">
                    {feature.title}
                  </h3>
                  
                  <p className="text-white/80 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Stats section */}
        <div className="bg-black/40 rounded-2xl p-8 backdrop-blur-sm border border-white/20">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-white mb-2">99.9%</div>
              <div className="text-white/80">Disponibilidade</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white mb-2">&lt; 1s</div>
              <div className="text-white/80">Tempo de Resposta</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white mb-2">24/7</div>
              <div className="text-white/80">Monitoramento</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-white mb-2">1000+</div>
              <div className="text-white/80">Veículos Ativos</div>
            </div>
          </div>
        </div>

        {/* Contact info */}
        <div className="text-center mt-12">
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6 text-white">
            <a 
              href="tel:(34)3327-0829" 
              className="flex items-center gap-2 hover:text-black transition-colors duration-300"
            >
              <Headphones className="w-5 h-5" />
              <span className="font-semibold">(34) 3327-0829</span>
            </a>
            
            <div className="w-1 h-1 bg-white rounded-full hidden sm:block"></div>
            
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              <span>Atendimento 24 horas</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServersSection;